import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faClipboardCheck, 
    faChartBar, 
    faClock, 
    faCheckCircle, 
    faUserCheck
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';

const ControllerDashboard = () => {
    const [stats, setStats] = useState({
        totalChecks: 0,
        todayChecks: 0,
        weekChecks: 0,
        monthChecks: 0,
        totalDays: 0,
        thisWeekDays: 0,
        thisMonthDays: 0,
        totalEarnings: 0,
    });
    const [controller, setController] = useState(null);
    const [recentChecks, setRecentChecks] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            
            // Fetch controller dashboard data
            const response = await api.get('/controle/dashboard');
            
            if (response.data.success) {
                setController(response.data.data.controller);
                // Map backend snake_case to frontend camelCase
                const backendStats = response.data.data.stats;
                setStats({
                    totalChecks: backendStats.total_checks || 0,
                    todayChecks: backendStats.today_checks || 0,
                    weekChecks: backendStats.week_checks || 0,
                    monthChecks: backendStats.month_checks || 0,
                    totalDays: backendStats.total_days || 0,
                    thisWeekDays: backendStats.this_week_days || 0,
                    thisMonthDays: backendStats.this_month_days || 0,
                    totalEarnings: backendStats.total_earnings || 0,
                });
                setRecentChecks(response.data.data.recent_checks || []);
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-MA', {
            style: 'currency',
            currency: 'MAD',
            minimumFractionDigits: 0
        }).format(amount);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
                        backgroundSize: '20px 20px'
                    }}></div>
                </div>
                <div className="text-center relative z-10">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Chargement du tableau de bord...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 relative overflow-hidden">
            {/* Dotted Background */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                    backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                }}></div>
            </div>

            <div className="relative z-10 p-4 sm:p-6">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="mb-6 sm:mb-8">
                        <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-2 sm:mb-3">
                            Tableau de Bord Contrôleur
                        </h1>
                        <p className="text-sm sm:text-base text-gray-600">
                            {controller ? `Bienvenue, ${controller.name}` : 'Gérez vos contrôles et suivez votre progression'}
                        </p>
                    </div>

                    {/* Bento Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                        {/* Control Activity Card */}
                        <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-xl border border-gray-100">
                            <div className="flex items-center justify-between mb-4 sm:mb-6">
                                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Activité des Contrôles</h2>
                                <FontAwesomeIcon icon={faChartBar} className="text-2xl sm:text-3xl text-red-600" />
                            </div>
                            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
                                <div className="text-center p-3 sm:p-4 bg-gray-50 rounded-xl">
                                    <div className="text-2xl sm:text-3xl font-bold text-gray-900">{stats.totalChecks || 0}</div>
                                    <div className="text-xs sm:text-sm text-gray-600">Total Contrôles</div>
                                </div>
                                <div className="text-center p-3 sm:p-4 bg-gray-50 rounded-xl">
                                    <div className="text-2xl sm:text-3xl font-bold text-gray-900">{stats.weekChecks || 0}</div>
                                    <div className="text-xs sm:text-sm text-gray-600">Cette Semaine</div>
                                </div>
                                <div className="text-center p-3 sm:p-4 bg-gray-50 rounded-xl">
                                    <div className="text-2xl sm:text-3xl font-bold text-gray-900">{stats.monthChecks || 0}</div>
                                    <div className="text-xs sm:text-sm text-gray-600">Ce Mois</div>
                                </div>
                                <div className="text-center p-3 sm:p-4 bg-gray-50 rounded-xl">
                                    <div className="text-2xl sm:text-3xl font-bold text-gray-900">{stats.todayChecks || 0}</div>
                                    <div className="text-xs sm:text-sm text-gray-600">Aujourd'hui</div>
                                </div>
                            </div>
                        </div>

                        {/* Attendance Overview */}
                        <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-xl border border-gray-100">
                            <div className="flex items-center justify-between mb-4 sm:mb-6">
                                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Présence et Gains</h3>
                                <FontAwesomeIcon icon={faUserCheck} className="text-2xl sm:text-3xl text-red-600" />
                            </div>
                            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
                                <div className="text-center p-3 sm:p-4 bg-gray-50 rounded-xl">
                                    <div className="text-2xl sm:text-3xl font-bold text-gray-900">{stats.totalDays || 0}</div>
                                    <div className="text-xs sm:text-sm text-gray-600">Total Jours</div>
                                </div>
                                <div className="text-center p-3 sm:p-4 bg-gray-50 rounded-xl">
                                    <div className="text-2xl sm:text-3xl font-bold text-gray-900">{stats.thisWeekDays || 0}</div>
                                    <div className="text-xs sm:text-sm text-gray-600">Cette Semaine</div>
                                </div>
                                <div className="text-center p-3 sm:p-4 bg-gray-50 rounded-xl">
                                    <div className="text-2xl sm:text-3xl font-bold text-gray-900">{stats.thisMonthDays || 0}</div>
                                    <div className="text-xs sm:text-sm text-gray-600">Ce Mois</div>
                                </div>
                                <div className="text-center p-3 sm:p-4 bg-gray-50 rounded-xl">
                                    <div className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 break-words">
                                        {stats.totalEarnings ? formatCurrency(stats.totalEarnings) : '0 MAD'}
                                    </div>
                                    <div className="text-xs sm:text-sm text-gray-600">Total Gains</div>
                                </div>
                            </div>
                        </div>

                        {/* Recent Activity */}
                        <div className="col-span-1 lg:col-span-2 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-xl border border-gray-200">
                            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Activité Récente</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                                {recentChecks.length > 0 ? (
                                    recentChecks.slice(0, 6).map((check) => (
                                        <div key={check.id} className="bg-white p-3 sm:p-4 rounded-xl border border-gray-200">
                                            <div className="flex items-center justify-between mb-2">
                                                <h4 className="font-medium text-gray-900 text-xs sm:text-sm">Contrôle #{check.id}</h4>
                                                <FontAwesomeIcon 
                                                    icon={faCheckCircle} 
                                                    className="text-xs sm:text-sm text-green-600 flex-shrink-0"
                                                />
                                            </div>
                                            <div className="text-xs text-gray-600 mb-2">
                                                {new Date(check.checked_at).toLocaleDateString('fr-FR', {
                                                    day: 'numeric',
                                                    month: 'short',
                                                    year: 'numeric'
                                                })}
                                            </div>
                                            <div className="flex justify-between items-center gap-2">
                                                <span className="text-xs text-gray-500 whitespace-nowrap">
                                                    {new Date(check.checked_at).toLocaleTimeString('fr-FR', {
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </span>
                                                <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800 whitespace-nowrap flex-shrink-0">
                                                    Effectué
                                                </span>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="col-span-full text-center py-6 sm:py-8 text-gray-500 text-sm sm:text-base">
                                        Aucune activité récente
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ControllerDashboard;
